import React, { useEffect, useState, useMemo } from "react";
import { auth, db } from "./firebase";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { Filter } from 'bad-words';
import { Helmet } from 'react-helmet-async';
import ListManager from "./components/ListManager";
import QuickAdd from "./components/QuickAdd";

function Profile() {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [displayName, setDisplayName] = useState("");
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [pendingDisplayName, setPendingDisplayName] = useState("");
  const [pendingPrivate, setPendingPrivate] = useState(false);
  const [pendingLocation, setPendingLocation] = useState("");
  const [courses, setCourses] = useState([]);
  const [customLists, setCustomLists] = useState({});
  const [deletedDefaultLists, setDeletedDefaultLists] = useState([]);
  const [playedListName, setPlayedListName] = useState('Played');
  const [wishlistListName, setWishlistListName] = useState('Bucket List');
  const [activeTab, setActiveTab] = useState("played");
  const [showListManager, setShowListManager] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      setSuccess("");
      setError("");
      if (firebaseUser) {
        const userRef = doc(db, "users", firebaseUser.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const userData = userSnap.data();
          setProfile(userData);
          setDisplayName(userData.displayName || "");
          setPendingDisplayName(userData.displayName || "");
          setPendingPrivate(userData.private || false);
          setPendingLocation(userData.location || "");
          setCustomLists(userData.customLists || {});
          setDeletedDefaultLists(userData.deletedDefaultLists || []);
          setPlayedListName(userData.playedListName || 'Played');
          setWishlistListName(userData.wishlistListName || 'Bucket List');
        }
      } else {
        setProfile(null);
        setDisplayName("");
        setPendingDisplayName("");
        setPendingPrivate(false);
        setPendingLocation("");
        setCustomLists({});
        setDeletedDefaultLists([]);
        setPlayedListName('Played');
        setWishlistListName('Bucket List');
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Fetch all courses on mount
  useEffect(() => {
    fetch(`${process.env.PUBLIC_URL}/courses.json`)
      .then(res => res.json())
      .then(data => setCourses(data));
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSuccess("");
    setError("");
    if (!pendingDisplayName || pendingDisplayName.length < 2) {
      setError("Display name must be at least 2 characters.");
      return;
    }
    // Profanity check (same as username)
    const filter = new Filter();
    filter.removeWords('hell', 'damn');
    if (filter.isProfane(pendingDisplayName)) {
      setError("Display name contains inappropriate language.");
      return;
    }
    try {
      const userRef = doc(db, "users", user.uid);
      await updateDoc(userRef, { 
        displayName: pendingDisplayName, 
        private: pendingPrivate,
        bio: profile.bio || '',
        location: pendingLocation
      });
      setSuccess("Profile updated!");
      setProfile((prev) => ({ 
        ...prev, 
        displayName: pendingDisplayName, 
        private: pendingPrivate,
        bio: profile.bio || '',
        location: pendingLocation
      }));
      setDisplayName(pendingDisplayName);
      setEditMode(false);
    } catch (err) {
      setError("Failed to update profile.");
    }
  };

  const handleCancel = () => {
    setPendingDisplayName(displayName);
    setPendingPrivate(profile && profile.private || false);
    setPendingLocation(profile && profile.location || "");
    setEditMode(false);
    setError("");
    setSuccess("");
  };

  const handleListsUpdate = (updatedLists) => {
    setCustomLists(updatedLists);
  };

  const handleLogout = async () => {
    await auth.signOut();
    window.location.reload();
  };

  // Helper to get course info by id
  const getCourseInfo = (courseId) => courses.find(c => c.course_id === courseId);

  // Function to add course to a list
  const addCourseToList = async (courseId, listType) => {
    if (!user || !profile) return;
    
    try {
      const userRef = doc(db, "users", user.uid);
      let updateData = {};
      
      if (listType === 'played') {
        const newPlayedCourses = [...playedCourses];
        if (!newPlayedCourses.includes(courseId)) {
          newPlayedCourses.push(courseId);
          updateData.playedCourses = newPlayedCourses;
        }
      } else if (listType === 'wishlist') {
        const newWishlistCourses = [...wishlistCourses];
        if (!newWishlistCourses.includes(courseId)) {
          newWishlistCourses.push(courseId);
          updateData.wishlistCourses = newWishlistCourses;
        }
      } else if (listType !== 'played' && listType !== 'wishlist' && customLists[listType]) {
        // Custom list
        const newCustomLists = { ...customLists };
        if (!newCustomLists[listType].courses.includes(courseId)) {
          newCustomLists[listType].courses.push(courseId);
          updateData.customLists = newCustomLists;
        }
      }
      
      if (Object.keys(updateData).length > 0) {
        await updateDoc(userRef, updateData);
        
        // Update local state
        if (updateData.playedCourses) {
          setProfile(prev => ({ ...prev, playedCourses: updateData.playedCourses }));
        }
        if (updateData.wishlistCourses) {
          setProfile(prev => ({ ...prev, wishlistCourses: updateData.wishlistCourses }));
        }
        if (updateData.customLists) {
          setCustomLists(updateData.customLists);
        }
        
        setSuccess(`Course added to ${listType === 'played' ? playedListName : listType === 'wishlist' ? wishlistListName : customLists[listType]?.name || 'list'}!`);
        setTimeout(() => setSuccess(""), 3000);
      }
    } catch (err) {
      setError("Failed to add course to list.");
      setTimeout(() => setError(""), 3000);
    }
  };

  // Function to remove course from a list
  const removeCourseFromList = async (courseId, listType) => {
    if (!user || !profile) return;
    
    try {
      const userRef = doc(db, "users", user.uid);
      let updateData = {};
      
      if (listType === 'played') {
        const newPlayedCourses = playedCourses.filter(id => id !== courseId);
        updateData.playedCourses = newPlayedCourses;
      } else if (listType === 'wishlist') {
        const newWishlistCourses = wishlistCourses.filter(id => id !== courseId);
        updateData.wishlistCourses = newWishlistCourses;
      } else if (listType !== 'played' && listType !== 'wishlist' && customLists[listType]) {
        // Custom list
        const newCustomLists = { ...customLists };
        newCustomLists[listType].courses = newCustomLists[listType].courses.filter(id => id !== courseId);
        updateData.customLists = newCustomLists;
      }
      
      await updateDoc(userRef, updateData);
      
      // Update local state
      if (updateData.playedCourses !== undefined) {
        setProfile(prev => ({ ...prev, playedCourses: updateData.playedCourses }));
      }
      if (updateData.wishlistCourses !== undefined) {
        setProfile(prev => ({ ...prev, wishlistCourses: updateData.wishlistCourses }));
      }
      if (updateData.customLists) {
        setCustomLists(updateData.customLists);
      }
      
      setSuccess(`Course removed from ${listType === 'played' ? playedListName : listType === 'wishlist' ? wishlistListName : customLists[listType]?.name || 'list'}!`);
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError("Failed to remove course from list.");
      setTimeout(() => setError(""), 3000);
    }
  };

  // Get played and wishlist arrays from profile
  const playedCourses = (profile && profile.playedCourses) || [];
  const wishlistCourses = (profile && profile.wishlistCourses) || [];
  
  // Calculate stats for played courses
  const stats = useMemo(() => {
    if (!playedCourses.length || !courses.length) {
      return {
        totalCourses: 0,
        states: new Set(),
        top1Courses: 0,
        top10Courses: 0,
        top50Courses: 0,
        top100Courses: 0,
        stateWithMostCourses: null,
        mostCoursesInState: 0,
        top25Courses: 0
      };
    }

    const courseInfo = playedCourses.map(id => courses.find(c => c.course_id === id)).filter(Boolean);
    const states = new Set(courseInfo.map(c => c.state).filter(Boolean));
    
    // Calculate courses per state
    const coursesPerState = {};
    courseInfo.forEach(course => {
      if (course.state) {
        coursesPerState[course.state] = (coursesPerState[course.state] || 0) + 1;
      }
    });
    
    // Find state with most courses
    let stateWithMostCourses = null;
    let mostCoursesInState = 0;
    Object.entries(coursesPerState).forEach(([state, count]) => {
      if (count > mostCoursesInState) {
        mostCoursesInState = count;
        stateWithMostCourses = state;
      }
    });
    
              const top1Courses = courseInfo.filter(c => c.top100_state_rank > 0 && c.top100_state_rank <= 1).length;
  const top10Courses = courseInfo.filter(c => c.top100_state_rank > 0 && c.top100_state_rank <= 10).length;
  const top25Courses = courseInfo.filter(c => c.top100_state_rank > 0 && c.top100_state_rank <= 25).length;
  const top50Courses = courseInfo.filter(c => c.top100_state_rank > 0 && c.top100_state_rank <= 50).length;
  const top100Courses = courseInfo.filter(c => c.top100_state_rank > 0 && c.top100_state_rank <= 100).length;
  
  // National ranking badge counts
  const top1NationalCourses = courseInfo.filter(c => c.GD100all_rank && c.GD100all_rank !== '' && parseInt(c.GD100all_rank) > 0 && parseInt(c.GD100all_rank) <= 1).length;
  const top10NationalCourses = courseInfo.filter(c => c.GD100all_rank && c.GD100all_rank !== '' && parseInt(c.GD100all_rank) > 0 && parseInt(c.GD100all_rank) <= 10).length;
  const top25NationalCourses = courseInfo.filter(c => c.GD100all_rank && c.GD100all_rank !== '' && parseInt(c.GD100all_rank) > 0 && parseInt(c.GD100all_rank) <= 25).length;
  const top50NationalCourses = courseInfo.filter(c => c.GD100all_rank && c.GD100all_rank !== '' && parseInt(c.GD100all_rank) > 0 && parseInt(c.GD100all_rank) <= 50).length;
  const top100NationalCourses = courseInfo.filter(c => c.GD100all_rank && c.GD100all_rank !== '' && parseInt(c.GD100all_rank) > 0 && parseInt(c.GD100all_rank) <= 100).length;
  
  // Public ranking badge counts
  const top1PublicCourses = courseInfo.filter(c => c.GD100pub_rank && c.GD100pub_rank !== '' && parseInt(c.GD100pub_rank) > 0 && parseInt(c.GD100pub_rank) <= 1).length;
  const top10PublicCourses = courseInfo.filter(c => c.GD100pub_rank && c.GD100pub_rank !== '' && parseInt(c.GD100pub_rank) > 0 && parseInt(c.GD100pub_rank) <= 10).length;
  const top25PublicCourses = courseInfo.filter(c => c.GD100pub_rank && c.GD100pub_rank !== '' && parseInt(c.GD100pub_rank) > 0 && parseInt(c.GD100pub_rank) <= 25).length;
  const top50PublicCourses = courseInfo.filter(c => c.GD100pub_rank && c.GD100pub_rank !== '' && parseInt(c.GD100pub_rank) > 0 && parseInt(c.GD100pub_rank) <= 50).length;
  const top100PublicCourses = courseInfo.filter(c => c.GD100pub_rank && c.GD100pub_rank !== '' && parseInt(c.GD100pub_rank) > 0 && parseInt(c.GD100pub_rank) <= 100).length;


    return {
      totalCourses: playedCourses.length,
      states,
      top1Courses,
      top10Courses,
      top50Courses,
      top100Courses,
      stateWithMostCourses,
      mostCoursesInState,
      top25Courses,
      top1NationalCourses,
      top10NationalCourses,
      top25NationalCourses,
      top50NationalCourses,
      top100NationalCourses,
      top1PublicCourses,
      top10PublicCourses,
      top25PublicCourses,
      top50PublicCourses,
      top100PublicCourses
    };
  }, [playedCourses, courses]);
  
  // Check URL parameters for initial tab and handle deleted tabs
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tab = urlParams.get('tab');
    if (tab === 'lists') {
      setActiveTab('lists');
    }
  }, []);

  // Handle case where active tab is deleted
  useEffect(() => {
    if (activeTab === 'played' && deletedDefaultLists.includes('played')) {
      // Switch to wishlist if available, otherwise first custom list, otherwise 'lists'
      if (!deletedDefaultLists.includes('wishlist')) {
        setActiveTab('wishlist');
      } else if (Object.keys(customLists).length > 0) {
        setActiveTab(Object.keys(customLists)[0]);
      } else {
        setActiveTab('lists');
      }
    } else if (activeTab === 'wishlist' && deletedDefaultLists.includes('wishlist')) {
      // Switch to played if available, otherwise first custom list, otherwise 'lists'
      if (!deletedDefaultLists.includes('played')) {
        setActiveTab('played');
      } else if (Object.keys(customLists).length > 0) {
        setActiveTab(Object.keys(customLists)[0]);
      } else {
        setActiveTab('lists');
      }
    }
  }, [activeTab, deletedDefaultLists, customLists]);

  // Helper function to sort courses by rank
  const sortCoursesByRank = (courseIds) => {
    return [...courseIds].sort((a, b) => {
      const ca = getCourseInfo(a);
      const cb = getCourseInfo(b);
      
      // First priority: GD100all_rank (National ranking)
      const aNationalRank = ca && ca.GD100all_rank && ca.GD100all_rank !== '' ? parseInt(ca.GD100all_rank) : 9999;
      const bNationalRank = cb && cb.GD100all_rank && cb.GD100all_rank !== '' ? parseInt(cb.GD100all_rank) : 9999;
      if (aNationalRank !== bNationalRank) return aNationalRank - bNationalRank;
      
      // Second priority: GD100pub_rank (Public ranking)
      const aPublicRank = ca && ca.GD100pub_rank && ca.GD100pub_rank !== '' ? parseInt(ca.GD100pub_rank) : 9999;
      const bPublicRank = cb && cb.GD100pub_rank && cb.GD100pub_rank !== '' ? parseInt(cb.GD100pub_rank) : 9999;
      if (aPublicRank !== bPublicRank) return aPublicRank - bPublicRank;
      
      // Third priority: top100_state_rank (State ranking)
      const aStateRank = ca && ca.top100_state_rank > 0 ? ca.top100_state_rank : 9999;
      const bStateRank = cb && cb.top100_state_rank > 0 ? cb.top100_state_rank : 9999;
      return aStateRank - bStateRank;
    });
  };

  // Sort playedCourses: ranked first by rank, then unranked
  const sortedPlayedCourses = sortCoursesByRank(playedCourses);
  // Sort wishlistCourses: ranked first by rank, then unranked
  const sortedWishlistCourses = sortCoursesByRank(wishlistCourses);

  // Sort custom lists by course count (most courses first)
  const sortedCustomLists = Object.values(customLists).sort((a, b) => {
    return b.courses.length - a.courses.length;
  });

  if (loading) {
    return <div style={{ textAlign: "center", marginTop: 40 }}>Loading profile...</div>;
  }
  if (!user) {
    return <div style={{ textAlign: "center", marginTop: 40 }}>You must be logged in to view your profile.</div>;
  }
  if (!user.emailVerified) {
    return <div style={{ textAlign: "center", marginTop: 40 }}>Please verify your email before accessing your profile.</div>;
  }
  if (!profile) {
    return <div style={{ textAlign: "center", marginTop: 40 }}>Profile not found.</div>;
  }

  // Use username, or fallback to email for avatar letter
  const avatarLetter = (profile.username && profile.username[0]) || (user.email && user.email[0]) || '?';
  const isMobile = window.innerWidth <= 768;

  return (
    <div style={{ 
      maxWidth: isMobile ? '100%' : 1200, 
      margin: isMobile ? "1rem 1rem 0 1rem" : "2rem auto 0 auto", 
      padding: isMobile ? '0' : '0 2rem',
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      gap: isMobile ? '1.5rem' : '3rem',
      alignItems: 'flex-start',
      boxSizing: 'border-box'
    }}>
      <Helmet>
        <title>Your Profile - Golf Near Me</title>
        <meta name="description" content="Manage your golf course lists, profile settings, and track your golfing progress." />
      </Helmet>
      {/* Profile Info Card */}
      <div style={{ 
        width: isMobile ? 'calc(100% - 2rem)' : 350,
        background: '#fff',
        borderRadius: 16,
        boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
        padding: isMobile ? '1.25rem' : '2rem'
      }}>
        {/* Header with Avatar and Username */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 16, 
          marginBottom: '2rem',
          position: 'relative'
        }}>
          {/* Avatar */}
          <div style={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: '#e5e7eb',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 36,
            fontWeight: 700,
            color: '#b0b4ba',
            textTransform: 'uppercase',
            flexShrink: 0,
          }}>{avatarLetter}</div>
          
          {/* Username Badge */}
          <div style={{
            background: '#f3f4f6',
            borderRadius: 12,
            padding: '8px 16px',
            display: 'flex',
            alignItems: 'center'
          }}>
            <span style={{ 
              fontSize: 16, 
              fontWeight: 600, 
              color: '#374151'
            }}>
              {profile.username || 'Username'}
            </span>
          </div>

          {/* Edit Button */}
          <button 
            onClick={() => setEditMode(true)} 
            style={{ 
              position: 'absolute',
              top: 0,
              right: 0,
              padding: '8px 16px', 
              borderRadius: 12, 
              border: 'none', 
              background: '#f3f4f6', 
              color: '#374151', 
              fontWeight: 600, 
              fontSize: 14, 
              cursor: 'pointer'
            }}
          >
            Edit
          </button>
        </div>

        {/* User Details */}
        <div style={{ marginBottom: '2rem' }}>
          <div style={{ marginBottom: '12px' }}>
            <span style={{ color: '#6b7280', fontSize: 14, fontWeight: 500 }}>Display Name</span>
            <div style={{ color: '#374151', fontSize: 16, fontWeight: 500, marginTop: '4px' }}>
              {displayName || 'Not set'}
            </div>
          </div>
          <div style={{ marginBottom: '12px' }}>
            <span style={{ color: '#6b7280', fontSize: 14, fontWeight: 500 }}>Location</span>
            <div style={{ color: '#6b7280', fontSize: 16, fontWeight: 500, marginTop: '4px' }}>
              {profile.location || 'Not set'}
            </div>
          </div>
        </div>

        {/* Bio Section - Only show if user has typed something */}
        {profile.bio && profile.bio.trim() && (
          <div>
            <div style={{ marginBottom: '12px' }}>
              <span style={{ color: '#6b7280', fontSize: 14, fontWeight: 600 }}>
                About <span style={{ color: '#8b5cf6', fontWeight: 600 }}>{displayName || 'Display Name'}</span>
              </span>
            </div>
            <div style={{ 
              color: '#6b7280', 
              fontSize: 15, 
              lineHeight: 1.6,
              fontStyle: 'italic'
            }}>
              {profile.bio}
            </div>
          </div>
        )}

        {/* Stats Section */}
        <div style={{ marginBottom: '2rem', marginTop: '1rem' }}>
          <div style={{ marginBottom: '12px' }}>
            <span style={{ color: '#6b7280', fontSize: 14, fontWeight: 600 }}>
              Course Stats
            </span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ 
              color: '#6b7280', 
              fontSize: 16, 
              fontWeight: 500, 
              marginTop: '4px' 
            }}>
              Has played {stats.totalCourses} course{stats.totalCourses !== 1 ? 's' : ''} across {stats.states.size} state{stats.states.size !== 1 ? 's' : ''}
            </div>
            <div style={{ 
              color: '#6b7280', 
              fontSize: 16, 
              fontWeight: 500, 
              marginTop: '4px' 
            }}>
              State with most courses played: {stats.stateWithMostCourses || 'N/A'} ({stats.mostCoursesInState || 0} course{(stats.mostCoursesInState || 0) !== 1 ? 's' : ''})
            </div>
          </div>
        </div>

        {/* National Ranking Badges Section */}
        {stats.top100NationalCourses > 0 && (
          <div style={{ marginBottom: '2rem', marginTop: '1rem' }}>
            <div style={{ 
              color: '#6b7280', 
              fontSize: 16, 
              fontWeight: 500, 
              marginTop: '4px' 
            }}>
              Has played {stats.top100NationalCourses} of the top 100 courses in America
            </div>
            <div style={{ 
              display: 'flex',
              flexWrap: 'wrap',
              gap: '2px',
              marginTop: '8px',
              marginLeft: '-0.5rem'
            }}>
              {stats.top1NationalCourses > 0 && (
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  gap: '4px',
                  marginRight: '-8px'
                }}>
                  <img 
                    src="/images/top1all_badge.png" 
                    alt="Top 1 National Badge" 
                    style={{ 
                      width: '70px', 
                      height: '70px',
                      objectFit: 'contain'
                    }}
                  />
                  <span style={{ 
                    color: '#6b7280', 
                    fontSize: '14px', 
                    fontWeight: '600',
                    textAlign: 'center'
                  }}>
                    {stats.top1NationalCourses}
                  </span>
                </div>
              )}
              
              {stats.top10NationalCourses > 0 && (
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  gap: '4px',
                  marginRight: '-8px'
                }}>
                  <img 
                    src="/images/top10all_badge.png" 
                    alt="Top 10 National Badge" 
                    style={{ 
                      width: '70px', 
                      height: '70px',
                      objectFit: 'contain'
                    }}
                  />
                  <span style={{ 
                    color: '#6b7280', 
                    fontSize: '14px', 
                    fontWeight: '600',
                    textAlign: 'center'
                  }}>
                    {stats.top10NationalCourses}
                  </span>
                </div>
              )}
              
              {stats.top25NationalCourses > 0 && (
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  gap: '4px',
                  marginRight: '-8px'
                }}>
                  <img 
                    src="/images/top25all_badge.png" 
                    alt="Top 25 National Badge" 
                    style={{ 
                      width: '70px', 
                      height: '70px',
                      objectFit: 'contain'
                    }}
                  />
                  <span style={{ 
                    color: '#6b7280', 
                    fontSize: '14px', 
                    fontWeight: '600',
                    textAlign: 'center'
                  }}>
                    {stats.top25NationalCourses}
                  </span>
                </div>
              )}
              
              {stats.top50NationalCourses > 0 && (
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  gap: '4px',
                  marginRight: '-8px'
                }}>
                  <img 
                    src="/images/top50all_badge.png" 
                    alt="Top 50 National Badge" 
                    style={{ 
                      width: '70px', 
                      height: '70px',
                      objectFit: 'contain'
                    }}
                  />
                  <span style={{ 
                    color: '#6b7280', 
                    fontSize: '14px', 
                    fontWeight: '600',
                    textAlign: 'center'
                  }}>
                    {stats.top50NationalCourses}
                  </span>
                </div>
              )}
              
              {stats.top100NationalCourses > 0 && (
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  gap: '4px',
                  marginRight: '-8px'
                }}>
                  <img 
                    src="/images/top100all_badge.png" 
                    alt="Top 100 National Badge" 
                    style={{ 
                      width: '70px', 
                      height: '70px',
                      objectFit: 'contain'
                    }}
                  />
                  <span style={{ 
                    color: '#6b7280', 
                    fontSize: '14px', 
                    fontWeight: '600',
                    textAlign: 'center'
                  }}>
                    {stats.top100NationalCourses}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Public Ranking Badges Section */}
        {stats.top100PublicCourses > 0 && (
          <div style={{ marginBottom: '2rem', marginTop: '1rem' }}>
            <div style={{ 
              color: '#6b7280', 
              fontSize: 16, 
              fontWeight: '500', 
              marginTop: '4px' 
            }}>
              Has played {stats.top100PublicCourses} of the top 100 public courses in America
            </div>
            <div style={{ 
              display: 'flex',
              flexWrap: 'wrap',
              gap: '2px',
              marginTop: '8px',
              marginLeft: '-0.5rem'
            }}>
              {stats.top1PublicCourses > 0 && (
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  gap: '4px',
                  marginRight: '-8px'
                }}>
                  <img 
                    src="/images/top1pub_badge.png" 
                    alt="Top 1 Public Badge" 
                    style={{ 
                      width: '70px', 
                      height: '70px',
                      objectFit: 'contain'
                    }}
                  />
                  <span style={{ 
                    color: '#6b7280', 
                    fontSize: '14px', 
                    fontWeight: '600',
                    textAlign: 'center'
                  }}>
                    {stats.top1PublicCourses}
                  </span>
                </div>
              )}
              
              {stats.top10PublicCourses > 0 && (
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  gap: '4px',
                  marginRight: '-8px'
                }}>
                  <img 
                    src="/images/top10pub_badge.png" 
                    alt="Top 10 Public Badge" 
                    style={{ 
                      width: '70px', 
                      height: '70px',
                      objectFit: 'contain'
                    }}
                  />
                  <span style={{ 
                    color: '#6b7280', 
                    fontSize: '14px', 
                    fontWeight: '600',
                    textAlign: 'center'
                  }}>
                    {stats.top10PublicCourses}
                  </span>
                </div>
              )}
              
              {stats.top25PublicCourses > 0 && (
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  gap: '4px',
                  marginRight: '-8px'
                }}>
                  <img 
                    src="/images/top25pub_badge.png" 
                    alt="Top 25 Public Badge" 
                    style={{ 
                      width: '70px', 
                      height: '70px',
                      objectFit: 'contain'
                    }}
                  />
                  <span style={{ 
                    color: '#6b7280', 
                    fontSize: '14px', 
                    fontWeight: '600',
                    textAlign: 'center'
                  }}>
                    {stats.top25PublicCourses}
                  </span>
                </div>
              )}
              
              {stats.top50PublicCourses > 0 && (
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  gap: '4px',
                  marginRight: '-8px'
                }}>
                  <img 
                    src="/images/top50pub_badge.png" 
                    alt="Top 50 Public Badge" 
                    style={{ 
                      width: '70px', 
                      height: '70px',
                      objectFit: 'contain'
                    }}
                  />
                  <span style={{ 
                    color: '#6b7280', 
                    fontSize: '14px', 
                    fontWeight: '600',
                    textAlign: 'center'
                  }}>
                    {stats.top50PublicCourses}
                  </span>
                </div>
              )}
              
              {stats.top100PublicCourses > 0 && (
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  gap: '4px',
                  marginRight: '-8px'
                }}>
                  <img 
                    src="/images/top100pub_badge.png" 
                    alt="Top 100 Public Badge" 
                    style={{ 
                      width: '70px', 
                      height: '70px',
                      objectFit: 'contain'
                    }}
                  />
                  <span style={{ 
                    color: '#6b7280', 
                    fontSize: '14px', 
                    fontWeight: '600',
                    textAlign: 'center'
                  }}>
                    {stats.top100PublicCourses}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* State Ranking Badges Section */}
        {stats.top100Courses > 0 && (
          <div style={{ marginBottom: '2rem', marginTop: '1rem' }}>
            <div style={{ 
              color: '#6b7280', 
              fontSize: 16, 
              fontWeight: 500, 
              marginTop: '4px' 
            }}>
              Has played {stats.top100Courses} in-state ranked course{stats.top100Courses !== 1 ? 's' : ''}
            </div>
            <div style={{ 
              display: 'flex',
              flexWrap: 'wrap',
              gap: '2px',
              marginTop: '8px',
              marginLeft: '-0.5rem'
            }}>
              {stats.top1Courses > 0 && (
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  gap: '4px',
                  marginRight: '-8px'
                }}>
                  <img 
                    src="/images/top1state_badge.png" 
                    alt="Top 1 Badge" 
                    style={{ 
                      width: '70px', 
                      height: '70px',
                      objectFit: 'contain'
                    }}
                  />
                  <span style={{ 
                    color: '#6b7280', 
                    fontSize: '14px', 
                    fontWeight: '600',
                    textAlign: 'center'
                  }}>
                    {stats.top1Courses}
                  </span>
                </div>
              )}
              
              {stats.top10Courses > 0 && (
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  gap: '4px',
                  marginRight: '-8px'
                }}>
                  <img 
                    src="/images/top10state_badge.png" 
                    alt="Top 10 Badge" 
                    style={{ 
                      width: '70px', 
                      height: '70px',
                      objectFit: 'contain'
                    }}
                  />
                  <span style={{ 
                    color: '#6b7280', 
                    fontSize: '14px', 
                    fontWeight: '600',
                    textAlign: 'center'
                  }}>
                    {stats.top10Courses}
                  </span>
                </div>
              )}
              
              {stats.top25Courses > 0 && (
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  gap: '4px',
                  marginRight: '-8px'
                }}>
                  <img 
                    src="/images/top25state_badge.png" 
                    alt="Top 25 Badge" 
                    style={{ 
                      width: '70px', 
                      height: '70px',
                      objectFit: 'contain'
                    }}
                  />
                  <span style={{ 
                    color: '#6b7280', 
                    fontSize: '14px', 
                    fontWeight: '600',
                    textAlign: 'center'
                  }}>
                    {stats.top25Courses}
                  </span>
                </div>
              )}
              
              {stats.top50Courses > 0 && (
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  gap: '4px',
                  marginRight: '-8px'
                }}>
                  <img 
                    src="/images/top50state_badge.png" 
                    alt="Top 50 Badge" 
                    style={{ 
                      width: '70px', 
                      height: '70px',
                      objectFit: 'contain'
                    }}
                  />
                  <span style={{ 
                    color: '#6b7280', 
                    fontSize: '14px', 
                    fontWeight: '600',
                    textAlign: 'center'
                  }}>
                    {stats.top50Courses}
                  </span>
                </div>
              )}
              
              {stats.top100Courses > 0 && (
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  gap: '4px',
                  marginRight: '-8px'
                }}>
                  <img 
                    src="/images/top100state_badge.png" 
                    alt="Top 100 Badge" 
                    style={{ 
                      width: '70px', 
                      height: '70px',
                      objectFit: 'contain'
                    }}
                  />
                  <span style={{ 
                    color: '#6b7280', 
                    fontSize: '14px', 
                    fontWeight: '600',
                    textAlign: 'center'
                  }}>
                    {stats.top100Courses}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Edit Mode Form */}
        {editMode && (
          <div style={{ 
            marginTop: '2rem', 
            padding: '2.5rem', 
            background: '#f9fafb', 
            borderRadius: 12,
            border: '1px solid #e5e7eb'
          }}>
            <h3 style={{ margin: '0 0 1rem 0', fontSize: 18, fontWeight: 600, color: '#374151' }}>
              Edit Profile
            </h3>
            
            {/* Email (read-only) */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', color: '#6b7280', fontSize: 14, fontWeight: 500, marginBottom: '4px' }}>
                Email
              </label>
              <input
                type="email"
                value={user.email}
                disabled
                style={{ 
                  width: '100%',
                  padding: '8px 12px',
                  borderRadius: 8,
                  border: '1px solid #d1d5db',
                  background: '#f3f4f6',
                  color: '#6b7280',
                  fontSize: 14
                }}
              />
            </div>

            {/* Display Name */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', color: '#6b7280', fontSize: 14, fontWeight: 500, marginBottom: '4px' }}>
                Display Name
              </label>
              <input
                type="text"
                value={pendingDisplayName}
                onChange={e => setPendingDisplayName(e.target.value)}
                style={{ 
                  width: '100%',
                  padding: '8px 12px',
                  borderRadius: 8,
                  border: '1px solid #d1d5db',
                  background: '#fff',
                  color: '#374151',
                  fontSize: 14
                }}
                autoFocus
              />
            </div>

            {/* Location */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', color: '#6b7280', fontSize: 14, fontWeight: 500, marginBottom: '4px' }}>
                Location
              </label>
              <input
                type="text"
                value={pendingLocation}
                onChange={e => setPendingLocation(e.target.value)}
                placeholder="e.g., Dallas, TX"
                style={{ 
                  width: '100%',
                  padding: '8px 12px',
                  borderRadius: 8,
                  border: '1px solid #d1d5db',
                  background: '#fff',
                  color: '#374151',
                  fontSize: 14
                }}
              />
            </div>

            {/* Bio */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', color: '#6b7280', fontSize: 14, fontWeight: 500, marginBottom: '4px' }}>
                Bio
              </label>
              <textarea
                value={profile.bio || ''}
                onChange={e => setProfile(prev => ({ ...prev, bio: e.target.value }))}
                placeholder="Tell us about yourself..."
                rows={4}
                style={{ 
                  width: '100%',
                  padding: '8px 12px',
                  borderRadius: 8,
                  border: '1px solid #d1d5db',
                  background: '#fff',
                  color: '#374151',
                  fontSize: 14,
                  resize: 'vertical'
                }}
              />
            </div>

            {/* Privacy Toggle */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#6b7280' }}>
                <input
                  type="checkbox"
                  checked={pendingPrivate}
                  onChange={e => setPendingPrivate(e.target.checked)}
                  style={{ width: 16, height: 16 }}
                />
                Make my profile private
              </label>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: 12 }}>
              <button 
                onClick={handleSave} 
                style={{ 
                  background: '#1D665D', 
                  color: '#fff', 
                  border: 'none', 
                  borderRadius: 8, 
                  padding: '10px 20px', 
                  fontWeight: 600, 
                  fontSize: 14, 
                  cursor: 'pointer',
                  flex: 1
                }}
              >
                Save
              </button>
              <button 
                onClick={handleCancel} 
                style={{ 
                  background: '#e5e7eb', 
                  color: '#6b7280', 
                  border: 'none', 
                  borderRadius: 8, 
                  padding: '10px 20px', 
                  fontWeight: 600, 
                  fontSize: 14, 
                  cursor: 'pointer',
                  flex: 1
                }}
              >
                Cancel
              </button>
            </div>

            {success && <p style={{ color: "#10b981", marginTop: 12, fontSize: 14 }}>{success}</p>}
            {error && <p style={{ color: "#ef4444", marginTop: 12, fontSize: 14 }}>{error}</p>}
          </div>
        )}
      </div>

      {/* Lists Section */}
      <div style={{ 
        width: isMobile ? 'calc(100% - 2rem)' : 700,
        background: '#fff', 
        borderRadius: 16, 
        boxShadow: '0 2px 10px rgba(0,0,0,0.08)', 
        padding: isMobile ? '1.25rem' : '1.5rem 1.5rem 1rem 1.5rem' 
      }}>
        {/* Tab Navigation */}
        <div style={{
          display: 'flex',
          borderBottom: '1px solid #eee',
          marginBottom: '1.5rem',
          overflowX: 'auto'
        }}>
          <button
            onClick={() => setShowListManager(true)}
            style={{
              background: 'none',
              border: 'none',
              padding: '12px 20px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '600',
              color: '#1D665D',
              borderBottom: '2px solid transparent',
              whiteSpace: 'nowrap'
            }}
          >
            + Manage Lists
          </button>
          {!deletedDefaultLists.includes('played') && (
            <button
              onClick={() => setActiveTab('played')}
              style={{
                background: 'none',
                border: 'none',
                padding: '12px 20px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: activeTab === 'played' ? '600' : '400',
                color: activeTab === 'played' ? '#1D665D' : '#666',
                borderBottom: activeTab === 'played' ? '2px solid #1D665D' : '2px solid transparent',
                whiteSpace: 'nowrap'
              }}
            >
{playedListName} ({sortedPlayedCourses.length})
            </button>
          )}
          {!deletedDefaultLists.includes('wishlist') && (
            <button
              onClick={() => setActiveTab('wishlist')}
              style={{
                background: 'none',
                border: 'none',
                padding: '12px 20px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: activeTab === 'wishlist' ? '600' : '400',
                color: activeTab === 'wishlist' ? '#1D665D' : '#666',
                borderBottom: activeTab === 'wishlist' ? '2px solid #1D665D' : '2px solid transparent',
                whiteSpace: 'nowrap'
              }}
            >
{wishlistListName} ({sortedWishlistCourses.length})
            </button>
          )}
          {sortedCustomLists.map(list => (
            <button
              key={list.id}
              onClick={() => setActiveTab(list.id)}
              style={{
                background: 'none',
                border: 'none',
                padding: '12px 20px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: activeTab === list.id ? '600' : '400',
                color: activeTab === list.id ? '#1D665D' : '#666',
                borderBottom: activeTab === list.id ? '2px solid #1D665D' : '2px solid transparent',
                whiteSpace: 'nowrap'
              }}
            >
              {list.name} ({list.courses.length})
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div>
          {activeTab === 'played' && !deletedDefaultLists.includes('played') ? (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <h2 style={{ fontSize: 20, fontWeight: 700, color: '#1D665D', margin: 0 }}>{playedListName}</h2>
                {sortedPlayedCourses.length > 0 && (
                  <button
                    onClick={() => {
                      // Create a dynamic live list URL that always shows current contents
                      window.location.href = `/map?list=live&type=played&user=${user.uid}`;
                    }}
                    style={{
                      background: '#1D665D',
                      color: 'white',
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}
                  >
                    View on Map
                  </button>
                )}
              </div>
              
              {/* Quick Add Section */}
              <QuickAdd 
                courses={courses}
                onAddCourse={addCourseToList}
                listType="played"
                listName={playedListName}
              />
              
              {/* Success/Error Messages for this list */}
              {success && activeTab === 'played' && (
                <div style={{ 
                  color: "#10b981", 
                  marginBottom: '12px', 
                  fontSize: '14px',
                  padding: '8px 12px',
                  background: '#f0fdf4',
                  border: '1px solid #bbf7d0',
                  borderRadius: '6px'
                }}>
                  {success}
                </div>
              )}
              {error && activeTab === 'played' && (
                <div style={{ 
                  color: "#ef4444", 
                  marginBottom: '12px', 
                  fontSize: '14px',
                  padding: '8px 12px',
                  background: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '6px'
                }}>
                  {error}
                </div>
              )}
              {sortedPlayedCourses.length === 0 ? (
                <div style={{ color: '#888', fontSize: 15, fontStyle: 'italic' }}>You haven't marked any courses as played yet.</div>
              ) : (
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {sortedPlayedCourses.map(cid => {
                    const c = getCourseInfo(cid);
                    return c ? (
                      <li key={cid} style={{ 
                        padding: '10px 0', 
                        borderBottom: '1px solid #f0f0f0',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start'
                      }}>
                        <div style={{ flex: 1 }}>
                          <span style={{ fontWeight: 600, color: '#222' }}>
                            <a href={`/course/${c.course_id}`} style={{ color: '#1D665D', textDecoration: 'underline' }}>{c.name}</a>
                          </span>
                          <span style={{ color: '#888', marginLeft: 8 }}>{c.club_name ? `at ${c.club_name} in ` : 'in '}{c.city}{c.city && c.state ? ', ' : ''}{c.state}</span>
                          <div style={{ fontSize: 14, color: '#1D665D', fontWeight: 500, margin: '2px 0 0 0' }}>
                            {c.GD100all_rank && c.GD100all_rank !== '' ? `#${c.GD100all_rank} in America` : null}
                            {c.GD100pub_rank && c.GD100pub_rank !== '' ? (c.GD100all_rank && c.GD100all_rank !== '' ? ` • #${c.GD100pub_rank} Public in America` : `#${c.GD100pub_rank} Public in America`) : null}
                            {c.top100_state_rank > 0 && c.state ? ((c.GD100all_rank && c.GD100all_rank !== '') || (c.GD100pub_rank && c.GD100pub_rank !== '') ? ` • #${c.top100_state_rank} in ${c.state}` : `#${c.top100_state_rank} in ${c.state}`) : null}
                          </div>
                        </div>
                        <button
                          onClick={() => removeCourseFromList(cid, 'played')}
                          style={{
                            background: 'none',
                            color: '#6b7280',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '22px',
                            fontWeight: 'bold',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginLeft: '12px',
                            flexShrink: 0,
                            padding: '6px'
                          }}
                          title="Remove from played list"
                        >
                          ×
                        </button>
                      </li>
                    ) : null;
                  })}
                </ul>
              )}
            </div>
          ) : activeTab === 'wishlist' && !deletedDefaultLists.includes('wishlist') ? (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <h2 style={{ fontSize: 20, fontWeight: 700, color: '#1D665D', margin: 0 }}>{wishlistListName}</h2>
                {sortedWishlistCourses.length > 0 && (
                  <button
                    onClick={() => {
                      // Create a dynamic live list URL that always shows current contents
                      window.location.href = `/map?list=live&type=wishlist&user=${user.uid}`;
                    }}
                    style={{
                      background: '#1D665D',
                      color: 'white',
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}
                  >
                    View on Map
                  </button>
                )}
              </div>
              
              {/* Quick Add Section */}
              <QuickAdd 
                courses={courses}
                onAddCourse={addCourseToList}
                listType="wishlist"
                listName={wishlistListName}
              />
              
              {/* Success/Error Messages for this list */}
              {success && activeTab === 'wishlist' && (
                <div style={{ 
                  color: "#10b981", 
                  marginBottom: '12px', 
                  fontSize: '14px',
                  padding: '8px 12px',
                  background: '#f0fdf4',
                  border: '1px solid #bbf7d0',
                  borderRadius: '6px'
                }}>
                  {success}
                </div>
              )}
              {error && activeTab === 'wishlist' && (
                <div style={{ 
                  color: "#ef4444", 
                  marginBottom: '12px', 
                  fontSize: '14px',
                  padding: '8px 12px',
                  background: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '6px'
                }}>
                  {error}
                </div>
              )}
              {sortedWishlistCourses.length === 0 ? (
                <div style={{ color: '#888', fontSize: 15, fontStyle: 'italic' }}>You haven't added any courses to your wishlist yet.</div>
              ) : (
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {sortedWishlistCourses.map(cid => {
                    const c = getCourseInfo(cid);
                    return c ? (
                      <li key={cid} style={{ 
                        padding: '10px 0', 
                        borderBottom: '1px solid #f0f0f0',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start'
                      }}>
                        <div style={{ flex: 1 }}>
                          <span style={{ fontWeight: 600, color: '#222' }}>
                            <a href={`/course/${c.course_id}`} style={{ color: '#1D665D', textDecoration: 'underline' }}>{c.name}</a>
                          </span>
                          <span style={{ color: '#888', marginLeft: 8 }}>{c.club_name ? `at ${c.club_name} in ` : 'in '}{c.city}{c.city && c.state ? ', ' : ''}{c.state}</span>
                          <div style={{ fontSize: 14, color: '#1D665D', fontWeight: 500, margin: '2px 0 0 0' }}>
                            {c.GD100all_rank && c.GD100all_rank !== '' ? `#${c.GD100all_rank} in America` : null}
                            {c.GD100pub_rank && c.GD100pub_rank !== '' ? (c.GD100all_rank && c.GD100all_rank !== '' ? ` • #${c.GD100pub_rank} Public in America` : `#${c.GD100pub_rank} Public in America`) : null}
                            {c.top100_state_rank > 0 && c.state ? ((c.GD100all_rank && c.GD100all_rank !== '') || (c.GD100pub_rank && c.GD100pub_rank !== '') ? ` • #${c.top100_state_rank} in ${c.state}` : `#${c.top100_state_rank} in ${c.state}`) : null}
                          </div>
                        </div>
                        <button
                          onClick={() => removeCourseFromList(cid, 'wishlist')}
                          style={{
                            background: 'none',
                            color: '#6b7280',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '22px',
                            fontWeight: 'bold',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginLeft: '12px',
                            flexShrink: 0,
                            padding: '6px'
                          }}
                          title="Remove from wishlist"
                        >
                          ×
                        </button>
                      </li>
                    ) : null;
                  })}
                </ul>
              )}
            </div>
          ) : activeTab !== 'played' && activeTab !== 'wishlist' && customLists[activeTab] ? (
            <div>
              <div style={{ marginBottom: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <h2 style={{ fontSize: 20, fontWeight: 700, color: '#1D665D', margin: 0 }}>
                    {customLists[activeTab].name}
                  </h2>
                  {customLists[activeTab].courses.length > 0 && (
                    <button
                      onClick={() => {
                        // Create a dynamic live list URL that always shows current contents
                        window.location.href = `/map?list=live&type=custom&listId=${activeTab}&user=${user.uid}`;
                      }}
                      style={{
                        background: '#1D665D',
                        color: 'white',
                        border: 'none',
                        padding: '8px 16px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '600'
                      }}
                    >
                      View on Map
                    </button>
                  )}
                </div>
              </div>
              
              {/* Quick Add Section */}
              <QuickAdd 
                courses={courses}
                onAddCourse={addCourseToList}
                listType={activeTab}
                listName={customLists[activeTab].name}
              />
              
              {/* Success/Error Messages for this list */}
              {success && activeTab !== 'played' && activeTab !== 'wishlist' && (
                <div style={{ 
                  color: "#10b981", 
                  marginBottom: '12px', 
                  fontSize: '14px',
                  padding: '8px 12px',
                  background: '#f0fdf4',
                  border: '1px solid #bbf7d0',
                  borderRadius: '6px'
                }}>
                  {success}
                </div>
              )}
              {error && activeTab !== 'played' && activeTab !== 'wishlist' && (
                <div style={{ 
                  color: "#ef4444", 
                  marginBottom: '12px', 
                  fontSize: '14px',
                  padding: '8px 12px',
                  background: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '6px'
                }}>
                  {error}
                </div>
              )}
              {customLists[activeTab].courses.length === 0 ? (
                <div style={{ color: '#888', fontSize: 15, fontStyle: 'italic' }}>
                  No courses in this list yet. Add courses from course detail pages!
                </div>
              ) : (
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {sortCoursesByRank(customLists[activeTab].courses).map(cid => {
                    const c = getCourseInfo(cid);
                    return c ? (
                      <li key={cid} style={{ 
                        padding: '10px 0', 
                        borderBottom: '1px solid #f0f0f0',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start'
                      }}>
                        <div style={{ flex: 1 }}>
                          <span style={{ fontWeight: 600, color: '#222' }}>
                            <a href={`/course/${c.course_id}`} style={{ color: '#1D665D', textDecoration: 'underline' }}>{c.name}</a>
                          </span>
                          <span style={{ color: '#888', marginLeft: 8 }}>{c.club_name ? `at ${c.club_name} in ` : 'in '}{c.city}{c.city && c.state ? ', ' : ''}{c.state}</span>
                          <div style={{ fontSize: 14, color: '#1D665D', fontWeight: 500, margin: '2px 0 0 0' }}>
                            {c.GD100all_rank && c.GD100all_rank !== '' ? `#${c.GD100all_rank} in America` : null}
                            {c.GD100pub_rank && c.GD100pub_rank !== '' ? (c.GD100all_rank && c.GD100all_rank !== '' ? ` • #${c.GD100pub_rank} Public in America` : `#${c.GD100pub_rank} Public in America`) : null}
                            {c.top100_state_rank > 0 && c.state ? ((c.GD100all_rank && c.GD100all_rank !== '') || (c.GD100pub_rank && c.GD100pub_rank !== '') ? ` • #${c.top100_state_rank} in ${c.state}` : `#${c.top100_state_rank} in ${c.state}`) : null}
                          </div>
                        </div>
                        <button
                          onClick={() => removeCourseFromList(cid, activeTab)}
                          style={{
                            background: 'none',
                            color: '#6b7280',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '22px',
                            fontWeight: 'bold',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginLeft: '12px',
                            flexShrink: 0,
                            padding: '6px'
                          }}
                          title={`Remove from ${customLists[activeTab].name}`}
                        >
                          ×
                        </button>
                      </li>
                    ) : null;
                  })}
                </ul>
              )}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
              <p>This list has been deleted or is no longer available.</p>
              <p>Please select another tab from the navigation above.</p>
            </div>
          )}
        </div>
      </div>

      {/* List Manager Modal */}
      <ListManager 
        isOpen={showListManager}
        onClose={() => setShowListManager(false)}
        onListsUpdate={handleListsUpdate}
      />
    </div>
  );
}

export default Profile; 